import React, { useEffect, useState } from 'react';
import axios from '../api';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  MenuItem,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { useUser } from './UserContext';
import CreateUserForm from './CreateUserForm';
import PageLoader from './ui/PageLoader';
import PageHeader from './ui/PageHeader';
import { pageContainerSx, tableHeaderSx } from '../styles/tokens';

const modalPaperSx = {
  borderRadius: 2,
  boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
  bgcolor: 'background.paper',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
};
const dialogTitleSx = {
  fontSize: { xs: '1rem', sm: '1.125rem' },
  fontWeight: 600,
  color: 'text.primary',
  px: { xs: 2, sm: 3 },
  py: { xs: 1.5, sm: 2 },
  borderBottom: '1px solid',
  borderColor: 'divider',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};
const inputFieldSx = { '& .MuiOutlinedInput-root': { borderRadius: 1.25, fontSize: '0.875rem', '& fieldset': { borderColor: 'divider' } } };

const UserManagement = () => {
  const { user } = useUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ role: '', password: '', mobile: '', address: '', is_active: true });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const roleOptions = roles.map((r) => ({ value: r.name, label: r.display_name || r.name }));

  const filteredUsers = users.filter((u) => {
    const search = searchTerm.toLowerCase();
    const username = (u.username || '').toLowerCase();
    const role = (u.role || '').toLowerCase();
    const roleLabel = (roleOptions.find((r) => r.value === u.role)?.label || '').toLowerCase();
    const mobile = (u.mobile || '').toLowerCase();
    const address = (u.address || '').toLowerCase();
    return username.includes(search) || role.includes(search) || roleLabel.includes(search) || mobile.includes(search) || address.includes(search);
  });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/auth/users');
      setUsers(res.data);
    } catch (err) {
      setError('Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await axios.get('/api/auth/roles');
      setRoles(res.data);
    } catch (err) {
      setRoles([]);
    }
  };

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchUsers();
      fetchRoles();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  const handleEdit = (u) => {
    setEditUser(u);
    setEditForm({ role: u.role, password: '', mobile: u.mobile || '', address: u.address || '', is_active: u.is_active !== false });
    setError('');
    setSuccess('');
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleEditSubmit = async () => {
    try {
      const payload = { role: editForm.role, is_active: editForm.is_active };
      if (editForm.password) payload.password = editForm.password;
      if (editForm.mobile !== undefined) payload.mobile = editForm.mobile;
      if (editForm.address !== undefined) payload.address = editForm.address;
      await axios.put(
        `/api/auth/users/${editUser.id}`,
        payload
      );
      setSuccess('Employee updated!');
      setEditUser(null);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update employee');
    }
  };

  const handleToggleActive = async (u) => {
    const newActive = !(u.is_active !== false);
    const action = newActive ? 'activate' : 'deactivate';
    if (!window.confirm(`Are you sure you want to ${action} this employee?`)) return;
    try {
      await axios.put(
        `/api/auth/users/${u.id}`,
        { is_active: newActive }
      );
      setSuccess(newActive ? 'Employee activated.' : 'Employee deactivated.');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${action} employee`);
    }
  };

  const handleCreateSuccess = () => {
    setCreateOpen(false);
    fetchUsers();
  };

  return (
    <Box sx={pageContainerSx}>
      <PageHeader
        title="Team"
        subtitle="Manage users and roles"
        actionLabel="Create User"
        actionIcon={<AddIcon sx={{ fontSize: 16 }} />}
        onAction={() => setCreateOpen(true)}
      />

      {/* Filters: Search - same format as Customer list */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch', gap: 1.25 }}>
        <TextField
          placeholder="Search users..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            flex: 1,
            minWidth: 0,
            maxWidth: { xs: '100%', sm: 280 },
            '& .MuiOutlinedInput-root': {
              height: 32,
              fontSize: '0.75rem',
              borderRadius: 1,
              '& fieldset': { borderColor: 'divider' },
              '& input': { py: 0.75 },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0.5 }}>
                <SearchIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 0 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 0 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper
        elevation={0}
        sx={{
          overflow: 'hidden',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.08)',
          width: '100%',
          minWidth: 0,
        }}
      >
        {loading ? (
          <PageLoader variant="section" message="Loading employees..." />
        ) : isMobile ? (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {filteredUsers.length === 0 ? (
              <Typography sx={{ py: 6, textAlign: 'center', color: 'text.secondary', fontSize: '0.875rem' }}>No employees available.</Typography>
            ) : (
              filteredUsers.map((u) => (
                <Paper
                  key={u.id}
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 2,
                    ...(u.is_active === false && { opacity: 0.85, bgcolor: 'action.hover' }),
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>{u.username}</Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>#{u.id} · {u.mobile || '—'}</Typography>
                      {(u.address || '').trim() && (
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.address}</Typography>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'text.secondary', bgcolor: 'action.hover', px: 1, py: 0.25, borderRadius: 1 }}>{u.role}</Box>
                        <Box component="span" sx={{ fontSize: '0.65rem', fontWeight: 500, bgcolor: u.is_active !== false ? 'success.main' : 'action.hover', color: u.is_active !== false ? 'white' : 'text.secondary', px: 1, py: 0.25, borderRadius: 1 }}>{u.is_active !== false ? 'Active' : 'Inactive'}</Box>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => handleEdit(u)} sx={{ width: 32, height: 32, color: 'text.secondary', '&:hover': { bgcolor: 'action.hover', color: 'text.primary' } }}>
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      {u.username !== user?.username && (
                        <IconButton size="small" onClick={() => handleToggleActive(u)} sx={{ width: 32, height: 32, color: u.is_active !== false ? 'error.main' : 'success.main', '&:hover': { bgcolor: 'action.hover' } }}>
                          {u.is_active !== false ? <DeleteIcon sx={{ fontSize: 18 }} /> : <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </Paper>
              ))
            )}
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={tableHeaderSx}>ID</TableCell>
                  <TableCell sx={tableHeaderSx}>Username</TableCell>
                  <TableCell sx={tableHeaderSx}>Mobile</TableCell>
                  <TableCell sx={tableHeaderSx}>Address</TableCell>
                  <TableCell sx={tableHeaderSx}>Role</TableCell>
                  <TableCell sx={tableHeaderSx}>Status</TableCell>
                  <TableCell align="center" sx={{ ...tableHeaderSx, width: 64 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>No employees available.</TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((u) => (
                    <TableRow key={u.id} sx={{ '&:hover': { bgcolor: 'action.hover' }, transition: 'background-color 0.15s', ...(u.is_active === false && { bgcolor: 'action.hover', opacity: 0.85 }) }}>
                      <TableCell sx={{ px: 2, py: 1.5 }}><Typography component="span" sx={{ fontSize: '0.9375rem', color: 'text.primary' }}>#{u.id}</Typography></TableCell>
                      <TableCell sx={{ px: 2, py: 1.5 }}><Typography component="span" sx={{ fontSize: '0.9375rem', color: 'text.primary' }}>{u.username}</Typography></TableCell>
                      <TableCell sx={{ px: 2, py: 1.5 }}><Typography component="span" sx={{ fontSize: '0.9375rem', color: 'text.secondary' }}>{u.mobile || '—'}</Typography></TableCell>
                      <TableCell sx={{ px: 2, py: 1.5, maxWidth: 180 }}><Typography component="span" sx={{ fontSize: '0.875rem', color: 'text.secondary', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.address || '—'}</Typography></TableCell>
                      <TableCell sx={{ px: 2, py: 1.5 }}><Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', color: 'text.secondary', bgcolor: 'action.hover', px: 1.25, py: 0.5, borderRadius: 1 }}>{u.role}</Box></TableCell>
                      <TableCell sx={{ px: 2, py: 1.5 }}><Box component="span" sx={{ fontSize: '0.8125rem', fontWeight: 400, bgcolor: u.is_active !== false ? 'success.main' : 'action.hover', color: u.is_active !== false ? 'white' : 'text.secondary', px: 1.25, py: 0.5, borderRadius: 1 }}>{u.is_active !== false ? 'Active' : 'Inactive'}</Box></TableCell>
                      <TableCell align="center" sx={{ px: 2, py: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          <IconButton size="small" onClick={() => handleEdit(u)} sx={{ width: 28, height: 28, color: 'text.secondary', borderRadius: 1.25, '&:hover': { bgcolor: 'action.hover', color: 'text.primary' } }}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                          {u.username !== user?.username && (
                            <IconButton size="small" onClick={() => handleToggleActive(u)} sx={{ width: 28, height: 28, color: u.is_active !== false ? 'error.main' : 'success.main', borderRadius: 1.25, '&:hover': { bgcolor: 'action.hover' } }}>
                              {u.is_active !== false ? <DeleteIcon sx={{ fontSize: 16 }} /> : <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid',
            borderColor: 'divider',
            px: 2,
            py: 1.25,
          }}
        >
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
            Showing{' '}
            <Typography component="span" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {filteredUsers.length}
            </Typography>{' '}
            employee{filteredUsers.length !== 1 ? 's' : ''}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton size="small" disabled sx={{ width: 28, height: 28, borderRadius: 1.25, color: 'text.secondary', '&.Mui-disabled': { color: 'text.disabled' }, '&:hover:not(.Mui-disabled)': { bgcolor: 'action.hover' } }}>
              <ChevronLeftIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <IconButton size="small" sx={{ width: 28, height: 28, borderRadius: 1.25, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: '0.8125rem', fontWeight: 500, '&:hover': { bgcolor: 'primary.dark' } }}>
              1
            </IconButton>
            <IconButton size="small" sx={{ width: 28, height: 28, borderRadius: 1.25, color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}>
              <ChevronRightIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      <Dialog
        open={!!editUser}
        onClose={() => setEditUser(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: modalPaperSx }}
        sx={{ '& .MuiDialog-paper': { mx: { xs: 1, sm: 2 }, width: { xs: 'calc(100% - 16px)', sm: '100%' } } }}
      >
        <DialogTitle sx={dialogTitleSx}>
          <span>Edit Employee</span>
          <IconButton size="small" onClick={() => setEditUser(null)} sx={{ color: 'text.secondary' }} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ overflow: 'auto', flex: 1, px: { xs: 2, sm: 3 }, py: 2 }}>
          <TextField
            select
            label="Role"
            name="role"
            value={editForm.role}
            onChange={handleEditChange}
            fullWidth
            size="small"
            sx={inputFieldSx}
          >
            {roleOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Mobile *"
            name="mobile"
            value={editForm.mobile}
            onChange={handleEditChange}
            fullWidth
            size="small"
            required
            sx={{ ...inputFieldSx, mt: 2 }}
          />
          <TextField
            label="Address"
            name="address"
            value={editForm.address}
            onChange={handleEditChange}
            fullWidth
            size="small"
            multiline
            minRows={2}
            sx={{ ...inputFieldSx, mt: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                name="is_active"
                checked={editForm.is_active}
                onChange={handleEditChange}
                size="small"
                disabled={editUser?.username === user?.username}
              />
            }
            label={editUser?.username === user?.username ? 'Active (you cannot deactivate yourself)' : 'Active (inactive employees cannot log in)'}
            sx={{ mt: 2, display: 'block' }}
          />
          <TextField
            label="New Password (leave blank to keep current)"
            name="password"
            type="password"
            value={editForm.password}
            onChange={handleEditChange}
            fullWidth
            size="small"
            sx={{ ...inputFieldSx, mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
          <Button onClick={() => setEditUser(null)} variant="outlined" sx={{ textTransform: 'none', fontWeight: 500 }}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSubmit} sx={{ textTransform: 'none', fontWeight: 500 }}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: modalPaperSx }}
        sx={{ '& .MuiDialog-paper': { mx: { xs: 1, sm: 2 }, width: { xs: 'calc(100% - 16px)', sm: '100%' } } }}
      >
        <DialogTitle sx={dialogTitleSx}>
          <span>Add Employee</span>
          <IconButton size="small" onClick={() => setCreateOpen(false)} sx={{ color: 'text.secondary' }} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ overflow: 'auto', flex: 1, px: { xs: 2, sm: 3 }, py: 2 }}>
          <CreateUserForm roles={roleOptions} onSuccess={handleCreateSuccess} />
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
          <Button onClick={() => setCreateOpen(false)} variant="outlined" sx={{ textTransform: 'none', fontWeight: 500 }}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
